import { useEffect, useMemo, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSearch, faExclamationTriangle, faPlus, faMinus } from "@fortawesome/free-solid-svg-icons";

import ErrorModal from "@/components/ErrorModal";
import SuccessModal from "@/components/SuccessModal";

import {
  createAllowedDomainRequest,
  getAllowedDomains,
  type AllowedDomainDto,
} from "@/api/allowedDomainsApi";

function normalizeDomain(input: string) {
  let s = (input ?? "").trim().toLowerCase();
  s = s.replace(/^https?:\/\//, "");
  const slash = s.indexOf("/");
  if (slash >= 0) s = s.slice(0, slash);
  if (s.startsWith("@")) s = s.slice(1);
  return s;
}

export default function AllowedDomainsPage() {
  const didLoadRef = useRef(false);

  const [domains, setDomains] = useState<AllowedDomainDto[]>([]);
  const [loading, setLoading] = useState(true);

  const [query, setQuery] = useState("");
  const [err, setErr] = useState<string | null>(null);

  // form
  const [action, setAction] = useState<"Add" | "Remove">("Add");
  const [requester, setRequester] = useState("");
  const [serviceNow, setServiceNow] = useState("");
  const [domainInput, setDomainInput] = useState("");

  const [submitting, setSubmitting] = useState(false);

  // modals
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorModalMsg, setErrorModalMsg] = useState<string>("");

  const [showSuccess, setShowSuccess] = useState(false);
  const [successMsg, setSuccessMsg] = useState<string>("Submitted!");

  useEffect(() => {
    if (didLoadRef.current) return;
    didLoadRef.current = true;

    (async () => {
      try {
        setLoading(true);
        setErr(null);

        const data = await getAllowedDomains();
        setDomains(
          [...data].sort((a, b) => a.domain.localeCompare(b.domain))
        );
      } catch (e: any) {
        setErr(e?.message ?? "Failed to load allowed domains.");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return domains;
    return domains.filter((d) => d.domain.toLowerCase().includes(q));
  }, [domains, query]);

  async function onSubmit() {
    if (submitting) return;

    const normalized = normalizeDomain(domainInput);
    const req = requester.trim();
    const sn = serviceNow.trim();

    // minimal validation
    if (!req) {
      setErrorModalMsg("Requester email is required.");
      setShowErrorModal(true);
      return;
    }
    if (!normalized) {
      setErrorModalMsg("Domain is required.");
      setShowErrorModal(true);
      return;
    }

    try {
      setSubmitting(true);
      setErr(null);

      const res = await createAllowedDomainRequest({
        action,
        requester: req,
        serviceNow: sn || null,
        domain: normalized,
      });

      setSuccessMsg(res.message || "Request has been sent!");
      setShowSuccess(true);

      // clear form
      setDomainInput("");
      setServiceNow("");
      setRequester("");
      setAction("Add");
    } catch (e: any) {
      setErrorModalMsg(e?.message ?? "Submit failed.");
      setShowErrorModal(true);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="container py-4">
      <div className="mb-3">
        <h1 className="h4 mb-1">Allowed Domains</h1>
        <div className="text-muted">
          Lookup what companies can communicate directly with ENMAX (allowed domains).
        </div>
      </div>

      {/* search */}
      <div className="input-group mb-3" style={{ maxWidth: 520 }}>
        <span className="input-group-text">
          <FontAwesomeIcon icon={faSearch} />
        </span>
        <input
          className="form-control"
          placeholder="Search domains..."
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
      </div>

      {err ? (
        <div className="alert alert-danger text-center">
          <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
          {err}
        </div>
      ) : null}

      <div className="row g-3">
        {/* list */}
        <div className="col-lg-7">
          <div className="card">
            <div className="card-body">
              <div className="d-flex align-items-center justify-content-between mb-2">
                <div className="fw-semibold">Domains</div>
                <div className="small text-muted">
                  {loading ? "Loading…" : `${filtered.length} shown`}
                </div>
              </div>

              {loading ? (
                <div className="text-muted">Loading…</div>
              ) : filtered.length === 0 ? (
                <div className="text-muted">No allowed domains found.</div>
              ) : (
                <div style={{ maxHeight: 480, overflow: "auto" }}>
                  <table className="table table-sm table-striped mb-0">
                    <thead>
                      <tr>
                        <th>Domain</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filtered.map((d) => (
                        <tr key={d.domain}>
                          <td className="font-monospace">{d.domain}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* form */}
        <div className="col-lg-5">
          <div className="card">
            <div className="card-body">
              <div className="fw-semibold mb-2">Request a change</div>
              <div className="text-muted small mb-3">
                Submitting creates an approval request to SecOps.
              </div>

              <div className="mb-2">
                <label className="form-label fw-semibold">ServiceNow number</label>
                <input
                  className="form-control"
                  placeholder="e.g. SCTASK0041119"
                  value={serviceNow}
                  onChange={(e) => setServiceNow(e.target.value)}
                />
              </div>

              <div className="mb-2">
                <label className="form-label fw-semibold">Requester (email)</label>
                <input
                  className="form-control"
                  placeholder="e.g. jsnow@enmax.com"
                  value={requester}
                  onChange={(e) => setRequester(e.target.value)}
                />
              </div>

              <div className="mb-2">
                <label className="form-label fw-semibold">Domain</label>
                <input
                  className="form-control"
                  placeholder="e.g. partner.com"
                  value={domainInput}
                  onChange={(e) => setDomainInput(e.target.value)}
                />
                <div className="form-text">
                  Tip: you can paste a URL; it will be normalized to a domain.
                </div>
              </div>

              <div className="mb-3">
                <label className="form-label fw-semibold">Action</label>
                <select
                  className="form-select"
                  value={action}
                  onChange={(e) => setAction(e.target.value as "Add" | "Remove")}
                >
                  <option value="Add">Add</option>
                  <option value="Remove">Remove</option>
                </select>
              </div>

              <button
                type="button"
                className={"btn " + (action === "Add" ? "btn-success" : "btn-danger")}
                onClick={onSubmit}
                disabled={submitting}
              >
                <FontAwesomeIcon icon={action === "Add" ? faPlus : faMinus} className="me-2" />
                {submitting ? "Submitting…" : "Submit request"}
              </button>
            </div>
          </div>
        </div>
      </div>

      <ErrorModal
        show={showErrorModal}
        title="Couldn't submit request"
        message={errorModalMsg}
        onClose={() => setShowErrorModal(false)}
      />

      <SuccessModal
        show={showSuccess}
        title="Request submitted"
        message={successMsg}
        onClose={() => setShowSuccess(false)}
        closeText="Close"
      />
    </div>
  );
}